import React, { useEffect, useState, useContext, useRef } from 'react';
import Fuse from 'fuse.js';
import './style.css';
import { AddressSearchContext } from '/src/App';
import ResourceCard from '/src/Components/ResourceCard/ResourceCard';
import { CircleX } from 'lucide-react';
import HelmetProvider from '/src/Components/HelmetProvider/HelmetProvider';
import { WindowSizeContext } from '/src/App';


const Resources = () => {

    const { deviceType } = useContext(WindowSizeContext);
    // Update the page title to "Resources"
    // useEffect(() => {
    //     document.title = "Resources";
    // }, []);

    const { portalData } = useContext(AddressSearchContext);
    const [allResources, setAllResources] = useState([]);
    const [filteredResources, setFilteredResources] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [resourcesPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilterSidebar, setShowFilterSidebar] = useState(false);
    const [openFilterPanels, setOpenFilterPanels] = useState({
        category: true,
        sub_category: false,
        climate_risk: false,
        house_part: false,
    });
    const [selectedFilters, setSelectedFilters] = useState({
        category: [],
        sub_category: [],
        climate_risk: [],
        house_part: [],
    });
    const sidebarRef = useRef(null);
    const sidebarTimeout = useRef(null);
    const filterToggleButtonRef = useRef(null);

    // Initialize Fuse instance
    const fuseOptions = {
        keys: [
            'title',
            'associated_house_categories',
            'associated_climate_risks'
        ],
        threshold: 0.4, // Lower = more strict matching
        includeScore: true
    };

    // If deviceType is desktop or tablet have the filter sidebar open by default
    useEffect(() => {
        if (deviceType === 'desktop' || deviceType === 'tablet') {
            setShowFilterSidebar(true);
        } else {
            setShowFilterSidebar(false);
        }
    }, [deviceType]);
    
    // Click outside to close sidebar
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                showFilterSidebar &&
                sidebarRef.current &&
                !sidebarRef.current.contains(event.target) &&
                filterToggleButtonRef.current &&
                !filterToggleButtonRef.current.contains(event.target)
            ) {
                setShowFilterSidebar(false);
            }
        };

        if (showFilterSidebar) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showFilterSidebar]);
    
    useEffect(() => {
        if (portalData.resources) {
            // Shuffle the resources once when they're loaded
            const shuffled = [...portalData.resources].sort(() => Math.random() - 0.5);
            setAllResources(shuffled);
            setFilteredResources(shuffled);
        }
    }, [portalData.resources]);

    // console.log('allResources ', allResources);

    // Extract unique filter options from allResources
    const getUniqueOptions = (key, isArray = false) => {
        const values = allResources.flatMap(resource => {
            if (isArray) return resource[key] || [];
            return resource[key] ? [resource[key]] : [];
        });
        return [...new Set(values.filter(v => !!v && v !== 0))].sort();
    };
    const categoryOptions = getUniqueOptions('category');
    const subCategoryOptions = getUniqueOptions('sub_category');
    const climateRiskOptions = getUniqueOptions('associated_climate_risks', true);
    const housePartOptions = getUniqueOptions('associated_house_categories', true);

    // Filtering logic (search + filters)
    useEffect(() => {
        let filtered = allResources;

        // Apply filters
        if (
            selectedFilters.category.length ||
            selectedFilters.sub_category.length ||
            selectedFilters.climate_risk.length ||
            selectedFilters.house_part.length
        ) {
            filtered = filtered.filter(resource => {
                // Category
                if (
                    selectedFilters.category.length &&
                    !selectedFilters.category.includes(resource.category)
                ) return false;
                // Sub-category
                if (
                    selectedFilters.sub_category.length &&
                    !selectedFilters.sub_category.includes(resource.sub_category)
                ) return false;
                // Climate risk (array)
                if (
                    selectedFilters.climate_risk.length &&
                    !resource.associated_climate_risks?.some(risk =>
                        selectedFilters.climate_risk.includes(risk)
                    )
                ) return false;
                // House part (array)
                if (
                    selectedFilters.house_part.length &&
                    !resource.associated_house_categories?.some(cat =>
                        selectedFilters.house_part.includes(cat)
                    )
                ) return false;
                return true;
            });
        }

        // Apply search
        if (searchTerm.trim() !== '') {
            const fuse = new Fuse(filtered, fuseOptions);
            const results = fuse.search(searchTerm);
            filtered = results.map(result => result.item);
        }

        setFilteredResources(filtered);
        setCurrentPage(1);
    }, [searchTerm, allResources, selectedFilters]);

    // Sidebar mouse leave logic
    const handleSidebarMouseLeave = () => {
        sidebarTimeout.current = setTimeout(() => {
            setShowFilterSidebar(false);
        }, 2000);
    };
    const handleSidebarMouseEnter = () => {
        if (sidebarTimeout.current) clearTimeout(sidebarTimeout.current);
    };

    // Accordion toggle
    const togglePanel = (panel) => {
        setOpenFilterPanels(prev => ({
            ...prev,
            [panel]: !prev[panel]
        }));
    };

    // Checkbox toggle
    const handleFilterChange = (type, value) => {
        setSelectedFilters(prev => {
            const arr = prev[type];
            return {
                ...prev,
                [type]: arr.includes(value)
                    ? arr.filter(v => v !== value)
                    : [...arr, value]
            };
        });
    };

    // Clear all filters
    const clearAllFilters = () => {
        setSelectedFilters({
            category: [],
            sub_category: [],
            climate_risk: [],
            house_part: [],
        });
    };

    // Get current resources
    const indexOfLastResource = currentPage * resourcesPerPage;
    const indexOfFirstResource = indexOfLastResource - resourcesPerPage;
    const currentResources = filteredResources.slice(indexOfFirstResource, indexOfLastResource);

    // Calculate page numbers to show
    const generatePageNumbers = () => {
        const totalPages = Math.ceil(filteredResources.length / resourcesPerPage);
        const currentPageNum = currentPage;
        const pageNumbers = [];

        if (totalPages <= 7) {
            // If 7 or fewer pages, show all
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            // Always show first page
            pageNumbers.push(1);

            if (currentPageNum > 3) {
                pageNumbers.push('...');
            }

            // Show pages around current page
            for (let i = Math.max(2, currentPageNum - 1); i <= Math.min(currentPageNum + 1, totalPages - 1); i++) {
                pageNumbers.push(i);
            }

            if (currentPageNum < totalPages - 2) {
                pageNumbers.push('...');
            }

            // Always show last page
            pageNumbers.push(totalPages);
        }

        return pageNumbers;
    };

    const clearSearch = () => {
        setSearchTerm('');
    }

    const introText = "We have over 500 blogs and videos covering everything from risks and resilence home improvements to how-to guides and financial explainers. Don't forget, when you search an address you'll see the materials that apply to that specific property! "

    return (
        <>
            <HelmetProvider
                pageType="resources"
            />
            <div className="static-page-content-frame">
                <h1>Resources</h1>
                <p className="resources-intro-text text-align-center">{introText}</p>
                <div className={`resources-header-row ${deviceType}`}>
                    <div className={`resources-search ${deviceType}`}>
                        <input
                            type="text"
                            placeholder="Search resources..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="resources-search-input"
                        />
                        {searchTerm && (
                            <div className="resources-search-clear-button-container">
                                <CircleX className="resources-search-clear-button" onClick={clearSearch} />
                            </div>
                        )}
                    </div>
                    <button
                        ref={filterToggleButtonRef}
                        className="resources-filter-toggle-btn"
                        onClick={() => setShowFilterSidebar(!showFilterSidebar)}
                    >
                        {showFilterSidebar ? 'Hide Filter Options' : 'Show Filter Options'}
                    </button>
                </div>

                {/* Selected Filters Summary */}
                {Object.values(selectedFilters).some(arr => arr.length > 0) && (
                    <div className="resources-filter-selected">
                        <span>Active Filters:</span>
                        {Object.entries(selectedFilters).map(([type, values]) =>
                            values.filter(Boolean).map(val => (
                                <span key={type + val} className="resources-filter-tag">
                                    {val}
                                </span>
                            ))
                        )}
                        <button className="resources-filter-clear" onClick={clearAllFilters}>
                            Clear All
                        </button>
                    </div>
                )}

                {/* Sidebar */}
                {showFilterSidebar && (
                    <div
                        className="resources-filter-sidebar"
                        ref={sidebarRef}
                        onMouseLeave={handleSidebarMouseLeave}
                        onMouseEnter={handleSidebarMouseEnter}
                    >
                        <button
                            className="resources-filter-hide-btn"
                            onClick={() => setShowFilterSidebar(false)}
                        >
                            Hide Filter
                        </button>
                        <div className="resources-filter-accordion">
                            {/* Category */}
                            <div className="resources-filter-panel">
                                <button
                                    className="resources-filter-panel-toggle"
                                    onClick={() => togglePanel('category')}
                                >
                                    Categories
                                </button>
                                {openFilterPanels.category && (
                                    <div className="resources-filter-panel-content">
                                        {categoryOptions.map(option => (
                                            <label key={option} className="resources-filter-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedFilters.category.includes(option)}
                                                    onChange={() => handleFilterChange('category', option)}
                                                />
                                                {option}
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {/* Sub-Category */}
                            <div className="resources-filter-panel">
                                <button
                                    className="resources-filter-panel-toggle"
                                    onClick={() => togglePanel('sub_category')}
                                >
                                    Sub-Categories
                                </button>
                                {openFilterPanels.sub_category && (
                                    <div className="resources-filter-panel-content">
                                        {subCategoryOptions.map(option => (
                                            <label key={option} className="resources-filter-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedFilters.sub_category.includes(option)}
                                                    onChange={() => handleFilterChange('sub_category', option)}
                                                />
                                                {option}
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {/* Climate Risks */}
                            <div className="resources-filter-panel">
                                <button
                                    className="resources-filter-panel-toggle"
                                    onClick={() => togglePanel('climate_risk')}
                                >
                                    Climate Risks
                                </button>
                                {openFilterPanels.climate_risk && (
                                    <div className="resources-filter-panel-content">
                                        {climateRiskOptions.map(option => (
                                            <label key={option} className="resources-filter-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedFilters.climate_risk.includes(option)}
                                                    onChange={() => handleFilterChange('climate_risk', option)}
                                                />
                                                {option}
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {/* House Part */}
                            <div className="resources-filter-panel">
                                <button
                                    className="resources-filter-panel-toggle"
                                    onClick={() => togglePanel('house_part')}
                                >
                                    House Part
                                </button>
                                {openFilterPanels.house_part && (
                                    <div className="resources-filter-panel-content">
                                        {housePartOptions.map(option => (
                                            <label key={option} className="resources-filter-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedFilters.house_part.includes(option)}
                                                    onChange={() => handleFilterChange('house_part', option)}
                                                />
                                                {option}
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="resources-main-grid">
                    {currentResources.map((resource) => (
                        <ResourceCard key={resource.id} resource={resource} />
                    ))}
                </div>

                {filteredResources.length === 0 ? (
                    <div className="no-results">
                        No resources found matching your search.
                    </div>
                ) : (
                    <div className="pagination">
                        <button
                            className="pagination-button"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>

                        {generatePageNumbers().map((number, index) => (
                            <button
                                key={index}
                                className={`pagination-button ${number === currentPage ? 'active' : ''} ${number === '...' ? 'dots' : ''}`}
                                onClick={() => number !== '...' && setCurrentPage(number)}
                                disabled={number === '...'}
                            >
                                {number}
                            </button>
                        ))}

                        <button
                            className="pagination-button"
                            onClick={() => setCurrentPage(prev =>
                                Math.min(prev + 1, Math.ceil(filteredResources.length / resourcesPerPage))
                            )}
                            disabled={currentPage === Math.ceil(filteredResources.length / resourcesPerPage)}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

export default Resources;